import { SUPER_PATHS } from "@router/superRoutes";
import { getOwnProperty } from "@utils/getOwnProperty";

/** Root route for the global Manage Settings workspace. */
export const SETTINGS_BASE_PATH = SUPER_PATHS.manageSettings;
const DEFAULT_SETTING_IDENTIFIER = "voice";

const SETTING_PATH_OVERRIDES = {
  [DEFAULT_SETTING_IDENTIFIER]: "voice-settings",
} satisfies Readonly<Record<string, string>>;

const SETTING_IDENTIFIER_OVERRIDES = new Map(
  Object.entries(SETTING_PATH_OVERRIDES).map(([identifier, path]) => [
    path,
    identifier,
  ]),
);

export const getSettingPathSegment = (identifier: string): string =>
  getOwnProperty(SETTING_PATH_OVERRIDES, identifier) ??
  identifier.replace(/_/g, "-");

export const getSettingIdentifierFromPath = (
  path: string | undefined,
): string | undefined =>
  path
    ? (SETTING_IDENTIFIER_OVERRIDES.get(path) ?? path.replace(/-/g, "_"))
    : undefined;

export const settingSectionPath = (identifier: string) =>
  `${SETTINGS_BASE_PATH}/${getSettingPathSegment(identifier)}`;

export const DEFAULT_MANAGE_SETTINGS_PATH = settingSectionPath(
  DEFAULT_SETTING_IDENTIFIER,
);

/**
 * The section to show for a URL identifier. An unknown or absent identifier
 * (bare `/super/manage-settings`, a stale bookmark, or an unavailable section)
 * falls back to the first visible section rather than erroring.
 */
export const resolveActiveSettingIdentifier = (
  selectedIdentifier: string | undefined,
  visibleIdentifiers: readonly string[],
) =>
  selectedIdentifier && visibleIdentifiers.includes(selectedIdentifier)
    ? selectedIdentifier
    : visibleIdentifiers[0];

export const getSettingEditorInstanceKey = (
  canonicalIdentifier: string,
  sourceIdentifier: string,
  isSourceBacked: boolean,
) =>
  isSourceBacked
    ? `${canonicalIdentifier}-${sourceIdentifier}`
    : canonicalIdentifier;

export const getSettingsLoadErrorCopy = (hasCachedSettings: boolean) =>
  hasCachedSettings
    ? {
        description:
          "The latest refresh failed, so the last loaded settings are still shown. Refresh the page to try again.",
        title: "Settings could not be refreshed",
      }
    : {
        description:
          "Full-Duplex remains available because it uses a separate settings service. Refresh the page to retry loading the other global settings.",
        title: "Other settings could not be loaded",
      };
